//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { TagsInput } from '@campaigns/components/shared/tags-input/TagsInput';
import { SAMPLING_CAMPAIGN_PRODUCT_DRAWER_DESTINATION_GTIN_TAGS_INPUT } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { URL_REGEX_RULE } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { BodyText, CaptionText } from '@components/atoms/text/Text';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';

interface ProductSyndicationFieldsProps {
  isViewOnly: boolean;
}

const getDisplayValue = (value?: string | null) =>
  value && value.trim() !== '' ? value : DEFAULT_EMPTY_VALUE;

export const ProductSyndicationFields = ({
  isViewOnly,
}: ProductSyndicationFieldsProps) => {
  const { getValues, setValue, watch } =
    useFormContext<EditableBVSamplingProduct>();

  const gtinValues =
    watch('destinationGTIN')
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean) ?? [];
  const pdpUrl = getValues('pdpUrl');

  const handleDestinationGtinChange = (tags: string[]) => {
    setValue('destinationGTIN', tags.join(', '), {
      shouldValidate: true,
    });
  };

  if (isViewOnly) {
    return (
      <>
        <FieldContainer>
          <BodyText>{ProductDetailsFieldLabels.destinationGTIN}</BodyText>
          <BodyText>
            {gtinValues.length > 0
              ? gtinValues.join(', ')
              : DEFAULT_EMPTY_VALUE}
          </BodyText>
        </FieldContainer>
        <FieldContainer>
          <BodyText>{ProductDetailsFieldLabels.pdpUrl}</BodyText>
          <BodyText>{getDisplayValue(pdpUrl)}</BodyText>
        </FieldContainer>
      </>
    );
  }

  return (
    <Card>
      <CaptionText>Optional</CaptionText>

      <TagsInput
        label={ProductDetailsFieldLabels.destinationGTIN}
        onTagsChange={handleDestinationGtinChange}
        placeholder="Enter all values where you want to see this product's reviews"
        selectedItemsLabel='Added to campaign request'
        trackingId={
          SAMPLING_CAMPAIGN_PRODUCT_DRAWER_DESTINATION_GTIN_TAGS_INPUT
        }
        values={gtinValues}
      />

      <FormField
        inputType='text'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.pdpUrl}
        name='pdpUrl'
        placeholder='https://your-awesome-store.com/130994'
        rules={{
          pattern: {
            message: 'Please enter a valid URL',
            value: URL_REGEX_RULE,
          },
        }}
      />
    </Card>
  );
};

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
  background: ${({ theme }) => theme.colors.gray0};
  border-radius: ${({ theme }) => theme.borderRadii[2]};
  padding: ${({ theme }) => theme.spacings[2]};
`;
